const CHECK_INTERVAL = 60_000;
const LAST_RELOAD_KEY = 'fanbird.deployment.lastReload';
const mainAsset = /\/static\/js\/main\.[a-f0-9]+\.js$/i;

export function startDeploymentRefresh(reload = () => window.location.reload()) {
  const loadedAsset = Array.from(document.scripts).map(script => {
    try { return new URL(script.src, window.location.href).pathname; } catch { return ''; }
  }).find(path => mainAsset.test(path));
  if (!loadedAsset) return () => {}; // Development has no versioned production bundle.
  const deployment = document.querySelector<HTMLMetaElement>('meta[name="fanbird-deployment"]')?.content;

  let candidate = '';
  let candidateSince = 0;
  let busy = false;
  let stopped = false;
  let reloading = false;
  let edited = false;
  let editedPath = window.location.pathname;
  const controller = new AbortController();
  const markEdited = (event: Event) => {
    if (event.target instanceof Element && event.target.closest('input, textarea, select, [contenteditable="true"]')) {
      edited = true;
      editedPath = window.location.pathname;
    }
  };
  const canReload = () => {
    const { pathname, search } = window.location;
    if (editedPath !== pathname) { edited = false; editedPath = pathname; }
    // These routes can own a stream, a payment or a one-use login callback.
    if (/^\/(?:live|bill|payment[^/]*|auth|signup|awake-handoff|tutorial)(?:\/|$)/i.test(pathname)
      || /^\/admin\/livelist\/(?:Live_manage|make)(?:\/|$)/i.test(pathname)
      || /(?:accountToken|ssoToken|refreshToken|code)=/.test(search)) return false;
    if (edited || document.hidden || navigator.onLine === false
      || document.querySelector('[role="dialog"], dialog[open], [aria-busy="true"]')
      || document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) return false;
    try {
      const lastReload = Number(sessionStorage.getItem(LAST_RELOAD_KEY));
      if (lastReload > 0 && Date.now() - lastReload < 300_000) return false;
    } catch { /* Storage may be unavailable in private browsing. */ }
    return true;
  };

  const check = async () => {
    if (busy || stopped || reloading || document.hidden || navigator.onLine === false) return;
    busy = true;
    try {
      const response = await fetch(deployment ? '/deployment-version.json' : '/asset-manifest.json', {
        cache: 'no-store', credentials: 'omit', signal: controller.signal,
      });
      if (!response.ok) throw new Error('Manifest unavailable');
      const manifest = await response.json();
      const value = manifest?.files?.['main.js'];
      if (typeof value !== 'string') throw new Error('Invalid manifest');
      const asset = new URL(value, window.location.origin);
      if (asset.origin !== window.location.origin || !mainAsset.test(asset.pathname)) throw new Error('Invalid main asset');
      const version = deployment ? manifest.version : asset.pathname;
      if (typeof version !== 'string' || (deployment && !/^v-\d+$/.test(version))) throw new Error('Invalid deployment');
      if (version === (deployment || loadedAsset)) { candidate = ''; return; }
      // Rolling deployments briefly serve two releases. Require two observations.
      if (candidate !== version) { candidate = version; candidateSince = Date.now(); return; }
      if (Date.now() - candidateSince < 10_000) return;
      if (!canReload()) return;
      const script = await fetch(asset.pathname, {
        method: 'HEAD', cache: 'no-store', credentials: 'omit', signal: controller.signal,
      });
      if (!script.ok || !/javascript/.test(script.headers.get('content-type') || '') || stopped || !canReload()) return;
      reloading = true;
      try { sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now())); } catch { /* Reload still works. */ }
      // Reload the same URL; authentication cookies and token storage stay intact.
      reload();
    } catch { candidate = ''; /* Offline/partial deployments keep the current page usable. */ }
    finally { busy = false; }
  };
  const onVisible = () => { if (!document.hidden) void check(); };
  const timer = window.setInterval(check, CHECK_INTERVAL);
  window.addEventListener('focus', onVisible);
  window.addEventListener('pageshow', onVisible);
  document.addEventListener('visibilitychange', onVisible);
  document.addEventListener('input', markEdited, true);
  document.addEventListener('change', markEdited, true);
  void check();
  return () => {
    stopped = true;
    controller.abort();
    window.clearInterval(timer);
    window.removeEventListener('focus', onVisible);
    window.removeEventListener('pageshow', onVisible);
    document.removeEventListener('visibilitychange', onVisible);
    document.removeEventListener('input', markEdited, true);
    document.removeEventListener('change', markEdited, true);
  };
}
